import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  RefreshCw, AlertTriangle, CheckCircle2, GitCompareArrows, Receipt, ChevronRight, Check, X,
} from 'lucide-react';
import { toast } from 'sonner';
import ProjectDetailModal from './ProjectDetailModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PnLReconciliation = ({ globalFY, getAuthHeaders, formatCurrency }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tolerance, setTolerance] = useState(1);
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [detailId, setDetailId] = useState(null); // crm project id for modal

  const fetchData = useCallback(async () => {
    if (!globalFY) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${API}/pnl/reconciliation?financial_year=${globalFY}&tolerance_pct=${tolerance}`,
        { headers: getAuthHeaders() }
      );
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load reconciliation');
    } finally {
      setLoading(false);
    }
  }, [globalFY, tolerance, getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt = (v) => formatCurrency(v);

  if (loading) {
    return (
      <div className="flex justify-center py-16" data-testid="recon-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }
  if (!data) return null;

  const s = data.summary;
  const revRows = onlyFlagged ? data.revenue_recon.filter(r => r.flagged) : data.revenue_recon;
  const costRows = onlyFlagged ? data.cost_recon.filter(r => r.flagged) : data.cost_recon;

  const DiffCell = ({ diff, flagged }) => (
    <span className={`tabular-nums font-medium ${flagged ? 'text-red-600' : 'text-emerald-600'}`}>
      {diff > 0 ? '+' : ''}{fmt(diff)}
    </span>
  );
  const FlagBadge = ({ flagged }) => flagged ? (
    <Badge className="bg-red-100 text-red-700 hover:bg-red-100 gap-1"><AlertTriangle className="w-3 h-3" />Mismatch</Badge>
  ) : (
    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1"><CheckCircle2 className="w-3 h-3" />OK</Badge>
  );

  return (
    <div className="space-y-8" data-testid="pnl-reconciliation">
      {/* Controls + summary */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <GitCompareArrows className="w-6 h-6 text-indigo-600" /> Data Reconciliation
          </h2>
          <p className="text-sm text-slate-500">Zoho Books estimates vs CRM, and CRM costs vs the expense ledger — FY {globalFY}</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs text-slate-500">Tolerance (%)</Label>
            <Input
              type="number" min="0" max="100" step="0.5" value={tolerance}
              onChange={(e) => setTolerance(parseFloat(e.target.value) || 0)}
              className="w-24" data-testid="recon-tolerance"
            />
          </div>
          <Button
            variant={onlyFlagged ? 'default' : 'outline'}
            onClick={() => setOnlyFlagged(v => !v)}
            data-testid="recon-toggle-flagged"
          >
            <AlertTriangle className="w-4 h-4 mr-1" /> {onlyFlagged ? 'Showing mismatches' : 'Only mismatches'}
          </Button>
          <Button variant="outline" size="icon" onClick={fetchData} data-testid="recon-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="recon-summary">
        <SummaryChip label="Revenue checks" value={s.revenue_checked} />
        <SummaryChip label="Revenue mismatches" value={s.revenue_flagged} danger={s.revenue_flagged > 0} />
        <SummaryChip label="Cost checks" value={s.cost_checked} />
        <SummaryChip label="Cost mismatches" value={s.cost_flagged} danger={s.cost_flagged > 0} />
        <SummaryChip label="Type discrepancies" value={s.type_discrepancies} danger={s.type_discrepancies > 0} />
        <SummaryChip label="ID link issues" value={s.id_linkage_flagged ?? 0} danger={(s.id_linkage_flagged ?? 0) > 0} />
      </div>

      {/* A. Estimate vs CRM revenue */}
      <section>
        <h3 className="text-lg font-semibold text-slate-900 mb-1">Estimate Revenue vs CRM Amount</h3>
        <p className="text-xs text-slate-500 mb-3">Sum of valid estimate revenue (excl GST) per project compared with CRM <span className="font-mono">Amount_without_GST</span>.</p>
        <Card className="border-slate-200 overflow-hidden">
          <Table data-testid="recon-revenue-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>CRM ID</TableHead>
                <TableHead>Project / Customer</TableHead>
                <TableHead className="text-center">Estimates</TableHead>
                <TableHead className="text-right">Estimate Revenue</TableHead>
                <TableHead className="text-right">CRM Amount</TableHead>
                <TableHead className="text-right">Difference</TableHead>
                <TableHead className="text-center">%</TableHead>
                <TableHead className="text-center">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {revRows.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">No linked estimates to reconcile</TableCell></TableRow>
              )}
              {revRows.map((r) => (
                <TableRow
                  key={r.crm_project_id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => setDetailId(r.crm_project_id)}
                  data-testid={`recon-rev-row-${r.crm_project_id}`}
                >
                  <TableCell className="font-mono text-xs">{r.crm_project_id}</TableCell>
                  <TableCell className="max-w-[220px] truncate">{r.training_name || r.customer_name || '—'}</TableCell>
                  <TableCell className="text-center tabular-nums">{r.estimate_count} <ChevronRight className="w-3 h-3 inline text-slate-400" /></TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(r.estimate_revenue)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(r.crm_amount_without_gst)}</TableCell>
                  <TableCell className="text-right"><DiffCell diff={r.difference} flagged={r.flagged} /></TableCell>
                  <TableCell className="text-center tabular-nums text-xs">{r.diff_pct}%</TableCell>
                  <TableCell className="text-center"><FlagBadge flagged={r.flagged} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </section>

      {/* B. CRM cost vs ledger */}
      <section>
        <h3 className="text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2">
          <Receipt className="w-5 h-5 text-slate-500" /> CRM Project Cost vs Expense Ledger
        </h3>
        <p className="text-xs text-slate-500 mb-3">CRM project cost compared with the sum of expenses booked against that CRM ID (projects with PO Date in FY).</p>
        <Card className="border-slate-200 overflow-hidden">
          <Table data-testid="recon-cost-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>CRM ID</TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>PO Date</TableHead>
                <TableHead className="text-right">CRM Cost</TableHead>
                <TableHead className="text-right">Ledger Expenses</TableHead>
                <TableHead className="text-right">Difference</TableHead>
                <TableHead className="text-center">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {costRows.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">No projects to reconcile</TableCell></TableRow>
              )}
              {costRows.map((r) => (
                <TableRow key={r.crm_project_id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(r.crm_project_id)} data-testid={`recon-cost-row-${r.crm_project_id}`}>
                  <TableCell className="font-mono text-xs">{r.crm_project_id}</TableCell>
                  <TableCell className="max-w-[200px] truncate">{r.training_name || '—'}</TableCell>
                  <TableCell className="text-xs">{r.type_of_project}</TableCell>
                  <TableCell className="text-xs">{r.po_date}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(r.crm_project_cost)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(r.ledger_expenses)}</TableCell>
                  <TableCell className="text-right"><DiffCell diff={r.difference} flagged={r.flagged} /></TableCell>
                  <TableCell className="text-center"><FlagBadge flagged={r.flagged} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </section>

      {/* C. Type discrepancies */}
      {data.type_discrepancies.length > 0 && (
        <section>
          <h3 className="text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" /> Project Type Discrepancies
          </h3>
          <p className="text-xs text-slate-500 mb-3">Estimate "Category" disagrees with the linked CRM "Type of Project". Category wins; please review.</p>
          <Card className="border-amber-200 overflow-hidden">
            <Table data-testid="recon-discrepancy-table">
              <TableHeader>
                <TableRow className="bg-amber-50">
                  <TableHead>Estimate #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>CRM ID</TableHead>
                  <TableHead>Category Type</TableHead>
                  <TableHead>CRM Type</TableHead>
                  <TableHead>Resolved</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.type_discrepancies.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-mono text-xs">{r.estimate_number}</TableCell>
                    <TableCell className="max-w-[180px] truncate">{r.customer_name}</TableCell>
                    <TableCell className="font-mono text-xs">{r.crm_project_id}</TableCell>
                    <TableCell><Badge variant="outline" className="text-emerald-700 border-emerald-200">{r.category_type}</Badge></TableCell>
                    <TableCell><Badge variant="outline" className="text-slate-600">{r.crm_type}</Badge></TableCell>
                    <TableCell className="font-medium">{r.resolved_type}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </section>
      )}

      {/* D. Project ID linkage across Estimate / Invoice / CRM */}
      {data.id_linkage?.length > 0 && (
        <section>
          <h3 className="text-lg font-semibold text-slate-900 mb-1 flex items-center gap-2">
            <GitCompareArrows className="w-5 h-5 text-slate-500" /> Project ID Linkage (Estimate · Invoice · CRM)
          </h3>
          <p className="text-xs text-slate-500 mb-3">Checks every CRM ID used in an estimate or invoice is present in CRM and correctly linked. Click a row for detail.</p>
          <Card className="border-slate-200 overflow-hidden">
            <Table data-testid="recon-linkage-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>CRM ID</TableHead>
                  <TableHead>Project / Customer</TableHead>
                  <TableHead className="text-center">In Estimate</TableHead>
                  <TableHead className="text-center">In Invoice</TableHead>
                  <TableHead className="text-center">In CRM</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(onlyFlagged ? data.id_linkage.filter(r => r.flagged) : data.id_linkage).map((r, i) => (
                  <TableRow key={i} className={`cursor-pointer hover:bg-slate-50 ${r.flagged ? 'bg-red-50' : ''}`} onClick={() => r.in_crm && setDetailId(r.crm_project_id)} data-testid={`recon-linkage-row-${r.crm_project_id}`}>
                    <TableCell className="font-mono text-xs">{r.crm_project_id}</TableCell>
                    <TableCell className="max-w-[220px] truncate">{r.name || '—'}</TableCell>
                    <TableCell className="text-center tabular-nums">{r.in_estimates || '—'}</TableCell>
                    <TableCell className="text-center tabular-nums">{r.in_invoices || '—'}</TableCell>
                    <TableCell className="text-center">{r.in_crm ? <Check className="w-4 h-4 text-emerald-600 inline" /> : <X className="w-4 h-4 text-red-600 inline" />}</TableCell>
                    <TableCell>
                      {r.issues?.length > 0
                        ? r.issues.map((iss, k) => <Badge key={k} className="bg-red-100 text-red-700 hover:bg-red-100 mr-1">{iss}</Badge>)
                        : (r.note ? <span className="text-xs text-slate-500">{r.note}</span> : <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Linked</Badge>)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </section>
      )}

      {/* Record detail modal (estimate + CRM + expenses + invoices) */}
      <ProjectDetailModal {...{ crmProjectId: detailId, getAuthHeaders, formatCurrency, open: !!detailId, onClose: () => setDetailId(null) }} />
    </div>
  );
};

const SummaryChip = ({ label, value, danger }) => (
  <div className={`rounded-xl border p-4 ${danger ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'}`}>
    <p className={`text-2xl font-bold tabular-nums ${danger ? 'text-red-600' : 'text-slate-900'}`}>{value}</p>
    <p className="text-xs text-slate-500 mt-0.5">{label}</p>
  </div>
);

export default PnLReconciliation;
